import { afterNextRender, ChangeDetectionStrategy, Component, ElementRef, inject, viewChild } from '@angular/core';
import { Popups } from '../popups';

/** what the buttons must not sit on: the films' timelines and buttons, the AR section's controls */
const KEEP_CLEAR = '[data-clear-fabs]';
/** the space kept between the buttons and such a control */
const GAP = 12;
/** moving up further than this would put the buttons in the middle of the picture: they step aside instead */
const MAX_LIFT = 100;
/** how long the chair of the back-to-top button takes to rise out of it */
const LIFT_OFF_MS = 1100;

/**
 * The floating buttons in the bottom right corner: two that open the Recliner
 * Finder and the Enquire Now popup and, above them once the first screen is
 * behind, the way back to the top — a recliner dozing inside a ring that
 * fills as the page goes down; it sits up under the pointer, and lifts off
 * when pressed.
 *
 * Where a full-screen section has controls of its own in that corner, the
 * buttons move up above them — or, on a phone, where there is no room, fade
 * out until the section has scrolled on.
 */
@Component({
  selector: 'app-fabs',
  templateUrl: './fabs.html',
  styleUrl: './fabs.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Fabs {
  protected readonly popups = inject(Popups);

  /** stays in the corner: its box is where the buttons rest */
  private readonly corner = viewChild.required<ElementRef<HTMLElement>>('corner');
  private readonly top = viewChild.required<ElementRef<HTMLElement>>('top');

  constructor() {
    afterNextRender(() => {
      const corner = this.corner().nativeElement;
      const top = this.top().nativeElement;
      let frame = 0;
      const update = () => {
        frame = 0;
        const lift = this.lift(corner.getBoundingClientRect());
        corner.style.setProperty('--lift', `${Math.min(lift, MAX_LIFT)}px`);
        corner.classList.toggle('is-away', lift > MAX_LIFT);

        // the way back up: there once the first screen is behind, its ring drawn as far round as the page is down
        const depth = document.documentElement.scrollHeight - window.innerHeight;
        top.classList.toggle('is-shown', window.scrollY > window.innerHeight * 0.9);
        top.style.setProperty('--depth', (depth > 0 ? Math.min(1, window.scrollY / depth) : 0).toFixed(3));
      };
      const schedule = () => {
        frame ||= requestAnimationFrame(update);
      };

      update();
      // (from here on a change of place is animated)
      requestAnimationFrame(() => corner.classList.add('is-settled'));
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      window.addEventListener('load', schedule);
    });
  }

  /** Back to the top of the page (smoothly: see styles.scss), the chair rising out of its button on the way. */
  protected toTop(): void {
    const top = this.top().nativeElement;
    top.classList.add('is-leaving');
    setTimeout(() => top.classList.remove('is-leaving'), LIFT_OFF_MS);
    window.scrollTo({ top: 0 });
    // (the button is about to go: nothing should be left holding the focus)
    top.blur();
  }

  /** How far the buttons have to move up from `rest`, their resting box, to sit on none of the page's controls. */
  private lift(rest: DOMRect): number {
    const controls = Array.from(document.querySelectorAll(KEEP_CLEAR))
      .map((el) => el.getBoundingClientRect())
      // (shown, and in the buttons' column)
      .filter((box) => box.width && box.height && box.right > rest.left - GAP && box.left < rest.right);

    let lift = 0;
    // moving clear of one control can land on the next one up, so look again — a few times at most
    for (let pass = 0; pass < 4 && lift <= MAX_LIFT; pass++) {
      const under = controls.filter((box) => box.bottom > rest.top - lift - GAP && box.top < rest.bottom - lift + GAP);
      if (!under.length) break;
      lift = rest.bottom - Math.min(...under.map((box) => box.top)) + GAP;
    }
    return lift;
  }
}
